/**
 * BOP-2 panels on a pressure-control component's page (api#1281):
 *
 *   PcExtensionsPanel  — D036 30-day extensions of the current certificate,
 *                        with "Record D036 extension" for an admin.
 *   PcComponentTestsPanel — the pressure tests this component was on, with a
 *                        way into recording one pre-filled with it.
 *
 * THE EXTENSION RULE IS THE API'S, and the button is offered only where the
 * API would accept it: a certificate on file, an expiry that is the
 * three-year IN-SERVICE expiry (not the total-life cap), and no
 * recertification outstanding. D036 Appendix 5: certification remains in
 * effect for a well operation in progress for up to 30 days past expiry when
 * unforeseen hole problems are met; longer needs prior AER approval and is
 * not recorded here. The date input's min/max say the same thing (base + 1 to
 * base + 30) so the browser cannot offer a day the API will refuse; the API
 * still decides.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, ClipboardCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { PcBadge, PcErrorLine } from './PcSectionNav';
import { usePcExtensions, usePcTests, useRecordPcExtension } from '../../hooks/usePressureControl';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { PC_RESULT_FILL, PC_RESULT_LABEL, PC_TESTS, pcDay, pcErrorMessage, pcTestKindLabel } from '../../lib/pressure-control';
import { addCalendarDays } from '../../lib/sales-activity';
import type { PcComponentDetail } from '../../types/pressure-control';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel, useOpenCount } from '../../pages/downhole/DownholeBits';

/** The day an extension is measured from: the expiry WITHOUT any extension. */
export const pcBaseExpiry = (c: PcComponentDetail) => c.certState?.baseExpiresOn ?? c.certState?.expiresOn ?? null;

/** Whether the API would accept an extension on this component today. */
export function pcCanExtend(c: PcComponentDetail): boolean {
  return (
    !!c.currentCertification &&
    c.certState?.expiryBasis === 'in_service' &&
    c.certState?.status !== 'recert_required' &&
    !!pcBaseExpiry(c)
  );
}

function RecordExtensionDialog({
  open, onClose, component, today,
}: { open: boolean; onClose: () => void; component: PcComponentDetail; today: string | null }) {
  const record = useRecordPcExtension(component.equipmentId);
  const openCount = useOpenCount(open);
  const base = pcBaseExpiry(component);
  const first = base ? addCalendarDays(base, 1) : null;
  const last = base ? addCalendarDays(base, 30) : null;
  const min = first && today && today > first ? today : first;
  const [through, setThrough] = useState('');
  const [reason, setReason] = useState('');
  const [contextRef, setContextRef] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setThrough(''); setReason(''); setContextRef(''); setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openCount]);

  const submit = async () => {
    setError(null);
    if (!through) return setError('Enter the new last valid day.');
    if (first && through < first) return setError(`The extension must run past the expiry (${pcDay(base)}).`);
    if (last && through > last) return setError(`D036 allows at most 30 days past the expiry, through ${pcDay(last)}. Longer needs AER approval.`);
    if (today && through < today) return setError('That day has already passed.');
    if (!reason.trim()) return setError('Describe the unforeseen hole problem.');
    if (!contextRef.trim()) return setError('Name the well operation in progress.');
    try {
      await record.mutateAsync({ extended_through: through, reason: reason.trim(), context_ref: contextRef.trim() });
      toast.success('Extension recorded');
      onClose();
    } catch (e) {
      setError(pcErrorMessage(e, 'The extension could not be recorded.'));
    }
  };

  return (
    <DhDialog
      open={open}
      onClose={onClose}
      title="Record a D036 extension"
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" onClick={submit} disabled={record.isPending} className="fi-action fi-action--standard fi-action--primary">
            {record.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record extension
          </button>
        </>
      }
    >
      <p className="mb-4 text-sm text-fieldiq-text" data-testid="pc-extension-rule">
        AER Directive 036, Appendix 5: certification remains in effect for a well operation in progress for up to 30 days past
        its expiry when unforeseen hole problems are met. Longer needs prior AER approval and is not recorded here.
      </p>
      <div className="grid gap-4">
        <DhField label="Extended through" hint={base ? `Expires ${pcDay(base)}. Choose a day up to ${pcDay(last)}.` : undefined}>
          <input
            type="date"
            className={DH_DATE_INPUT}
            value={through}
            min={min ?? undefined}
            max={last ?? undefined}
            onChange={(e) => setThrough(e.target.value)}
            aria-label="Extended through"
          />
        </DhField>
        <DhField label="Unforeseen hole problem">
          <textarea className={`${DH_INPUT} h-20 py-2`} value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} aria-label="Unforeseen hole problem" />
        </DhField>
        <DhField label="Well operation in progress" hint="A well, job or tour reference.">
          <input className={DH_INPUT} value={contextRef} maxLength={100} onChange={(e) => setContextRef(e.target.value)} aria-label="Well operation in progress" />
        </DhField>
      </div>
      <PcErrorLine message={error} />
    </DhDialog>
  );
}

export function PcExtensionsPanel({
  component, canAdmin, today,
}: { component: PcComponentDetail; canAdmin: boolean; today: string | null }) {
  const tz = useTenantTimeZone();
  const [recording, setRecording] = useState(false);
  const query = usePcExtensions(component.equipmentId);
  const failure = queryLoadFailure(query);
  const rows = query.data ?? [];
  const offer = canAdmin && pcCanExtend(component);

  return (
    <DhPanel
      eyebrow="D036 · 30 days at most"
      title="Certificate extensions"
      testId="pc-extensions-panel"
      actions={
        offer ? (
          <button type="button" onClick={() => setRecording(true)} className={DH_SECONDARY}>
            <CalendarPlus className="size-4" aria-hidden="true" />
            Record D036 extension
          </button>
        ) : undefined
      }
      footer={rows.length > 0 ? 'Newest first · a later extension of the same certificate replaces an earlier one; a new certificate ends both' : undefined}
    >
      {query.isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading extensions" />
        </div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="extensions" />
      ) : rows.length === 0 ? (
        <DhEmpty>
          No extension on record.
          {canAdmin && !pcCanExtend(component) &&
            ' Only a certificate limited by its three-year in-service expiry, with no recertification outstanding, can be extended.'}
        </DhEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Extended through</th>
                <th scope="col" className="px-4 py-3">Expiry it extends</th>
                <th scope="col" className="px-4 py-3">Well operation</th>
                <th scope="col" className="px-4 py-3">Hole problem</th>
                <th scope="col" className="px-4 py-3">Recorded</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.id} className="border-t border-fieldiq-border" data-testid="pc-extension-row">
                  <td className="px-4 py-3 text-sm font-semibold text-white">{pcDay(x.extendedThrough)}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDay(x.baseExpiresOn)}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{x.contextRef || '—'}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{x.reason || '—'}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{formatInstant(x.createdAt, tz) ?? pcDay(x.grantedOn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {offer && <RecordExtensionDialog open={recording} onClose={() => setRecording(false)} component={component} today={today} />}
    </DhPanel>
  );
}

export function PcComponentTestsPanel({ component, canRecord }: { component: PcComponentDetail; canRecord: boolean }) {
  const tz = useTenantTimeZone();
  const query = usePcTests({ equipment_id: component.equipmentId });
  const failure = queryLoadFailure(query);
  const rows = query.data ?? [];

  return (
    <DhPanel
      eyebrow="Append-only"
      title="Pressure tests"
      testId="pc-component-tests-panel"
      actions={
        canRecord ? (
          <Link to={`${PC_TESTS}/new?component=${component.equipmentId}`} className={DH_SECONDARY}>
            <ClipboardCheck className="size-4" aria-hidden="true" />
            Record test
          </Link>
        ) : undefined
      }
      footer={rows.length > 0 ? `${rows.length} shown · newest first` : undefined}
    >
      {query.isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading tests" />
        </div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="pressure tests" />
      ) : rows.length === 0 ? (
        <DhEmpty>This component has not been on a recorded pressure test.</DhEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rows.map((t) => {
            const mine = t.items.find((i) => i.equipmentId === component.equipmentId);
            return (
              <li key={t.id}>
                <Link
                  to={`${PC_TESTS}/${t.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm hover:bg-fieldiq-dark/50"
                  data-testid="pc-component-test-row"
                >
                  <span className="font-semibold text-white">
                    {formatInstant(t.testedAt, tz) ?? '—'} · {pcTestKindLabel(t.testKind)}
                  </span>
                  <span className="flex items-center gap-2">
                    {!mine && <span className="text-xs text-fieldiq-muted">as the stack or gauge</span>}
                    <PcBadge value={mine?.result ?? t.result} labels={PC_RESULT_LABEL} fills={PC_RESULT_FILL} />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </DhPanel>
  );
}
